import React from "react";
import { trpc } from "@/lib/trpc";
import { parsePublicationTracking } from "../../../shared/publicationTracking";

export function PublicationTrackingCard({
  url,
  json,
  topicId,
}: {
  url?: string | null;
  json?: string | null;
  topicId?: number;
}) {
  const parsed = parsePublicationTracking(json);
  const result = parsed?.url === url ? parsed : null;
  const checks = [
    ["發布內容", result?.publication],
    ["搜尋存取", result?.indexability],
    ["Sitemap", result?.sitemap],
    ["內部連結", result?.internalLink],
    ["Google 搜尋", result?.search],
  ] as const;
  const formatTime = (raw: string) =>
    new Intl.DateTimeFormat("zh-TW", {
      timeZone: "Asia/Taipei",
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    }).format(new Date(raw));
  const staleRun =
    result?.state === "running" &&
    Date.now() - Date.parse(result.startedAt) >= 10 * 60_000;
  return (
    <div className="rounded-md border p-4">
      <p className="font-medium">發布與搜尋追蹤</p>
      {url && topicId ? <PublicationTrackingRerunButton topicId={topicId} /> : null}
      {!url ? (
        <p className="mt-2 text-sm text-muted-foreground">尚未填入發布網址</p>
      ) : (
        <>
          <p className="mt-1 text-xs text-muted-foreground">
            每小時安排檢查，每篇每天複查
          </p>
          {(!result || result.state === "running") && (
            <p className="mt-2 text-sm">
              {!result
                ? "等待自動檢查"
                : staleRun
                  ? "上次檢查中斷，等待排程重試"
                  : "檢查中"}
            </p>
          )}
          <dl className="mt-3 space-y-3">
            {checks.map(([name, check]) => (
              <div key={name}>
                <div className="flex justify-between gap-3 text-sm">
                  <dt>{name}</dt>
                  <dd
                    className={
                      check?.status === "pass"
                        ? "text-emerald-700"
                        : check?.status === "issue"
                          ? "text-amber-700"
                          : "text-muted-foreground"
                    }
                  >
                    {name === "發布內容" && check?.status === "pass" ? "Yes" :
                      name === "發布內容" && check?.status === "issue" && ["網址錯誤", "文章標題不符", "正文與原稿不同"].includes(check.label) ? "No" :
                      check?.label || "尚未檢查"}
                  </dd>
                </div>
                {check && (
                  <dd className="mt-1 break-words text-xs leading-5 text-muted-foreground">
                    {check.detail}
                  </dd>
                )}
              </div>
            ))}
          </dl>
          <p className="mt-3 text-xs text-muted-foreground">
            最後檢查時間：
            {result?.checkedAt
              ? `${formatTime(result.checkedAt)}（台灣時間）`
              : "尚無檢查紀錄"}
          </p>
        </>
      )}
    </div>
  );
}

function PublicationTrackingRerunButton({ topicId }: { topicId: number }) {
  const utils = trpc.useUtils();
  const mutation = trpc.articleWorkbench.recheckPublication.useMutation({
    onSuccess: async () => { await utils.articleWorkbench.list.invalidate(); },
  });
  return <div className="mt-2 text-xs">
    <button type="button" className="rounded border px-3 py-1.5 disabled:opacity-50" disabled={mutation.isPending} onClick={() => mutation.mutate({ id: topicId })}>
      {mutation.isPending ? "正在正式環境檢查…" : "重新檢查"}
    </button>
    {mutation.error ? <p role="alert" className="mt-1 text-red-700">檢查未完成：{mutation.error.message}</p> : null}
    {mutation.data?.checked === 0 ? <p className="mt-1 text-muted-foreground">本次沒有可重跑的文章，或檢查已在執行</p> : null}
  </div>;
}
